import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { InstitutionPerson } from '../../../api/app-types/institution.app';
import { NeighborContext } from '../../../api/app-types/neighbors.app';
import { INSTITUTION_PERSON_SERVICE } from '../../../api/services/institution-person.service';
import { PaginatedResponse } from '../../pagination/paginated-response';
import { LocalStorageService } from '../../storage/local-storage.service';

const resolveInitialPageSize = (
	storage: LocalStorageService,
	auth: MsalService | null,
	preferenceKey: string,
	defaultPageSize: number,
	pageSizeOptions: number[],
) => {
	const userId = storage.resolveCurrentUserId(auth);
	if (!userId) return defaultPageSize;

	const saved = storage.getPageSize(userId, preferenceKey);
	return saved && pageSizeOptions.includes(saved) ? saved : defaultPageSize;
};

export const institutionPersonsResolver: ResolveFn<PaginatedResponse<InstitutionPerson>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(INSTITUTION_PERSON_SERVICE);
	const env = inject(ENVIRONMENT);
	const storage = inject(LocalStorageService);
	const auth = inject(MsalService, { optional: true });
	const id = Number(_route.paramMap.get('id')!);
	const initialRange = resolveInitialPageSize(
		storage,
		auth,
		'institution-persons-list',
		env.defaultPageSize,
		env.pageSizeOptions,
	);
	const response = await api.getAll(id, 0, initialRange);
	return response;
};

export const institutionPersonResolver: ResolveFn<InstitutionPerson> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(INSTITUTION_PERSON_SERVICE);
	const institutionId = Number(_route.paramMap.get('id')!);
	const personId = Number(_route.paramMap.get('personId')!);
	const response = await api.getById(institutionId, personId);
	return response;
};

export const institutionPersonNeighborsResolver: ResolveFn<NeighborContext<InstitutionPerson>> = async (
	route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(INSTITUTION_PERSON_SERVICE);
	const institutionId = route.paramMap.get('id')!;
	const personId = route.paramMap.get('personId')!;
	return api.getInstitutionPersonNeighbors(institutionId, personId);
};
